import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Container, Row, Col } from 'react-bootstrap';
import styles from './Team.module.css';

const Team = ({ navigateTo }) => {
    const [selectedMember, setSelectedMember] = useState(null);

    // Gérer le scroll du body et l'effet navbar quand le modal est ouvert
    React.useEffect(() => {
        if (selectedMember) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }

        // Cleanup au démontage du composant
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [selectedMember]);

    // Fermer le modal avec la touche Escape
    React.useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && selectedMember) {
                setSelectedMember(null);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [selectedMember]);

    const teamMembers = [
        {
            id: 1,
            name: "Djallal BOUCHENAK, CPI",
            title: "CPI en Structure - Génie Civil",
            image: "https://ui-avatars.com/api/?name=Djallal+Bouchenak&size=400&background=007bff&color=ffffff&bold=true&font-size=0.38&rounded=false",
            specialties: [],
            education: [
                "Maîtrise en génie de la construction – École de technologie supérieure (ÉTS), Montréal, Canada",
                "Diplôme d'Ingénieur d'État et Master II – École Nationale Supérieure des Travaux Publics, Alger",
                "Étude Préparatoire en Sciences de l'Ingénieur – École Préparatoire Sciences et Techniques, Oran"
            ],
            experience: "5+ années d'expérience",
            certifications: ["Ing. OIQ (Ordre des ingénieurs du Québec)", "Carte ASP (Attestation santé sécurité)", "Verified International Academic Qualifications"],
            bio: "CPI en structure – génie civil, spécialisé en conception, analyse et dimensionnement de structures pour des projets de bâtiments. Maîtrise des logiciels de modélisation 3D, d'analyse par éléments finis et de développement BIM, notamment pour l'évaluation des sollicitations, la vérification de la stabilité et l'optimisation des éléments porteurs. Expérience en rédaction de notes de calcul, en production de plans d'exécution (coffrage, ferraillage, assemblages) et en estimation des quantitatifs. Approche rigoureuse, technique et orientée vers des solutions fiables, conformes et bien coordonnées avec les autres disciplines.",
            achievements: [
                "Élaborer la conception structurelle générale et réaliser le prédimensionnement des éléments porteurs (dalles, poutres, poteaux, fondations).",
                "Modéliser la structure en 3D à l'aide d'un logiciel d'analyse par éléments finis, et définir l'ensemble des charges ainsi que leurs combinaisons selon les normes en vigueur.",
                "Effectuer l'analyse structurale par éléments finis pour identifier les sollicitations (moment de flexion, effort normal, effort tranchant) dans chaque élément.",
                "Vérifier la résistance et la stabilité de chaque élément structurel face aux sollicitations calculées.",
                "Rédiger les notes de calcul détaillées, incluant les hypothèses, méthodes, résultats et justifications des dimensionnements.",
                "Développer le modèle 3D BIM (sur Revit) avec le détail du ferraillage et des assemblages pour coordonner avec les autres disciplines.",
                "Produire les plans d'exécution 2D (plans de coffrage, de ferraillage, de détails des assemblages).",
                "Établir les quantitatifs et les métrés des matériaux (béton, acier, ferraillage) pour les besoins du budget."
            ]
        },
        {
            id: 2,
            name: "Hamza El Gass, ing., M. ing., MBA",
            title: "Ingénieur Structures & BIM Manager",
            disabled: true,
            image: "https://ui-avatars.com/api/?name=Hamza+El+Gass&size=400&background=6c757d&color=ffffff&bold=true&font-size=0.38&rounded=false",
            specialties: ["Conception et analyse de structures", "Structures Béton", "Gestion de Projet"],
            education: [
                "M.Sc. Génie de la Construction - ÉTS",
                "B.Ing. Génie Civil - Université McGill",
                "Certification Autodesk Revit Professional"
            ],
            experience: "5+ années d'expérience",
            certifications: ["Ing. OIQ", "Autodesk Certified Professional", "PRINCE2"],
            bio: "Spécialiste en modélisation 3D et gestion de projets complexes. Pionnière dans l'implémentation du BIM au Québec avec une expertise approfondie en structures de béton armé.",
            achievements: [
                "Implémentation BIM pour 30+ projets",
                "Formation de 200+ professionnels au BIM",
                "Prix Innovation ÉTS 2023"
            ]
        },
        {
            id: 3,
            name: "Romain-Ange Yao, ing.",
            title: "Ingénieur en Structures",
            image: "https://ui-avatars.com/api/?name=Romain+Yao&size=400&background=0056b3&color=ffffff&bold=true&font-size=0.38&rounded=false",
            specialties: [],
            savoirFaire: [
                "Analyse et conception de structures acier et béton armé",
                "Inspection et surveillance de chantier",
                "Diagnostics et expertises structurelles",
                "Maintien d'actif"
            ],
            education: [
                "Maîtrise en génie de la construction option pont et bâtiment – École de technologie supérieure (ÉTS), Montréal, Canada. En cours",
                "Baccalauréat Génie Civil – Université du Québec à Chicoutimi (UQAC)"
            ],
            experience: "5+ années d'expérience",
            certifications: ["Ing. OIQ (Ordre des ingénieurs du Québec)", "Carte ASP (Attestation santé sécurité)"],
            bio: "Ingénieur en structure spécialisé en maintien d'actif, en analyse et conception des structures avec une solide expérience pour les projets de bâtiments. Maîtrise des logiciels de modélisation 2D et de gestion de projet. Expérience en surveillance de chantier et coordination BIM.",
            achievements: []
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        }
    };

    return (
        <section className={styles.teamSection} style={{ paddingTop: '120px' }}>
            <Container>
                {/* Header Section */}
                <Row className="justify-content-center">
                    <Col lg={8} className="text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className={styles.sectionHeader}
                        >
                            <div className="page-eyebrow">
                                <span className="page-eyebrow-line"></span>
                                <span className="page-eyebrow-text">DHR Structures | Notre Équipe</span>
                                <span className="page-eyebrow-dot"></span>
                            </div>
                            <h1 className={styles.mainTitle}>
                                Notre Engagement d'Excellence
                                <span className={styles.highlightText}> Au Cœur de Vos Projets</span>
                            </h1>
                            <p className={styles.sectionDescription}>
                                Chez DHR Structures, nous croyons que la réussite de vos projets repose sur l'expertise et la vision de nos ingénieurs. Notre équipe s'engage à allier innovation technique, rigueur méthodologique et approche collaborative pour répondre à vos défis les plus complexes avec diligence.
                            </p>
                        </motion.div>
                    </Col>
                </Row>

                {/* Team Members Cards */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className={styles.teamGrid}
                >
                    <Row className="justify-content-center">
                        {teamMembers.map((member) => (
                            <Col lg={4} md={6} key={member.id} className="mb-5">
                                <motion.div
                                    variants={cardVariants}
                                    className={`${styles.teamCard} ${member.disabled ? styles.teamCardDisabled : ''}`}
                                    whileHover={member.disabled ? {} : { y: -10 }}
                                    transition={{ duration: 0.3 }}
                                    onClick={() => !member.disabled && setSelectedMember(member)}
                                >
                                    <div className={styles.cardHeader}>
                                        <div className={styles.imageContainer}>
                                            <img
                                                src={member.image}
                                                alt={member.name}
                                                className={styles.memberImage}
                                            />
                                            {!member.disabled && (
                                                <div className={styles.imageOverlay}>
                                                    <div className={styles.viewDetails}>
                                                        <i className="bi bi-eye"></i>
                                                        <span>Voir le profil</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.memberInfo}>
                                            <h3 className={styles.memberName}>{member.name}</h3>
                                            <p className={styles.memberTitle}>{member.title}</p>
                                            <div className={styles.experienceBadge}>{member.experience}</div>
                                        </div>
                                    </div>

                                    <div className={styles.cardBody}>
                                        <button
                                            className={styles.detailsButton}
                                            disabled={member.disabled}
                                            style={member.disabled ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : {}}
                                        >
                                            <i className={`bi ${member.disabled ? 'bi-clock' : 'bi-plus-circle'}`}></i>
                                            {member.disabled ? 'Profil bientôt disponible' : 'Découvrir le parcours'}
                                        </button>
                                    </div>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </motion.div>

                {/* Section Valeurs de l'équipe */}
                <Row className="justify-content-center my-5">
                    <Col lg={10}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className={styles.valuesSection}
                        >
                            <h2 className={styles.valuesTitle}>Nos Valeurs : Le Fondement de Notre Engagement</h2>
                            <p className={styles.valuesSubtitle}>Chez DHR Structures, notre approche s'articule autour de trois piliers essentiels qui guident chacune de nos interventions et garantissent la réussite de vos projets.</p>
                            <Row>
                                <Col md={4} className={styles.valueItem}>
                                    <div className={styles.valueIcon}>
                                        <i className="bi bi-lightbulb"></i>
                                    </div>
                                    <h4>Ingéniosité</h4>
                                    <p>Nous concevons des solutions structurales créatives et adaptées, en repoussant les limites techniques par une approche imaginative et novatrice.</p>
                                </Col>
                                <Col md={4} className={styles.valueItem}>
                                    <div className={styles.valueIcon}>
                                        <i className="bi bi-award"></i>
                                    </div>
                                    <h4>Maîtrise</h4>
                                    <p>Notre expertise technique approfondie et notre précision méthodologique garantissent la fiabilité et la pérennité de chaque réalisation, du calcul à l'exécution.</p>
                                </Col>
                                <Col md={4} className={styles.valueItem}>
                                    <div className={styles.valueIcon}>
                                        <i className="bi bi-graph-up-arrow"></i>
                                    </div>
                                    <h4>Performance</h4>
                                    <p>Nous nous engageons à livrer des projets optimisés, efficaces et respectueux des échéanciers et des budgets, sans jamais compromettre la qualité.</p>
                                </Col>
                            </Row>
                        </motion.div>
                    </Col>
                </Row>

                {/* Call to Action */}
                <motion.div
                    className="section-cta"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                >
                    <div className="section-cta-inner">
                        <h3>Prêt à concrétiser votre projet ?</h3>
                        <p>Notre équipe d'ingénieurs expérimentés est à votre écoute pour étudier vos besoins et vous accompagner de la conception à la livraison.</p>
                        <div className="section-cta-buttons">
                            <button className="section-cta-btn-outline" onClick={() => navigateTo('projects')}>
                                NOS DOMAINES D'INTERVENTION
                            </button>
                            <button className="section-cta-btn-solid" onClick={() => navigateTo('contact')}>
                                NOUS CONTACTER
                            </button>
                        </div>
                    </div>
                </motion.div>
            </Container>

            {/* Modal pour les détails du membre */}
            {selectedMember && (
                <div
                    className={styles.modalOverlay}
                    onClick={(e) => { if (e.target === e.currentTarget) setSelectedMember(null); }}
                >
                    <motion.div
                        className={styles.modalContent}
                        initial={{ opacity: 0, scale: 0.8, y: 50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 50 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Bouton fermeture */}
                        <div className={styles.modalTopBar}>
                            <button
                                className={styles.closeButton}
                                onClick={() => setSelectedMember(null)}
                                type="button"
                                aria-label="Fermer"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        {/* Zone scrollable */}
                        <div className={styles.modalScroll}>
                        <div className={styles.modalHeader}>
                            <img
                                src={selectedMember.image}
                                alt={selectedMember.name}
                                className={styles.modalImage}
                            />
                            <div className={styles.modalMemberInfo}>
                                <h2>{selectedMember.name}</h2>
                                <p className={styles.modalTitle}>{selectedMember.title}</p>
                                <div className={styles.modalExperienceBadge}>
                                    {selectedMember.experience}
                                </div>
                            </div>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.modalSection}>
                                <h3>À propos</h3>
                                <p>{selectedMember.bio}</p>
                            </div>

                            {selectedMember.specialties && selectedMember.specialties.length > 0 && (
                            <div className={styles.modalSection}>
                                <h3><i className="bi bi-gear"></i> Spécialités</h3>
                                <div className={styles.modalSpecialtyTags}>
                                    {selectedMember.specialties.map((specialty, index) => (
                                        <span key={index} className={styles.modalSpecialtyTag}>
                                            {specialty}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            )}

                            <div className={styles.modalSection}>
                                <h3><i className="bi bi-mortarboard"></i> Formation académique</h3>
                                <ul className={styles.modalList}>
                                    {selectedMember.education.map((degree, index) => (
                                        <li key={index}>{degree}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className={styles.modalSection}>
                                <h3><i className="bi bi-award"></i> Certifications professionnelles</h3>
                                <div className={styles.modalCertBadges}>
                                    {selectedMember.certifications.map((cert, index) => (
                                        <span key={index} className={styles.modalCertBadge}>
                                            {cert}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {selectedMember.savoirFaire && selectedMember.savoirFaire.length > 0 && (
                            <div className={styles.modalSection}>
                                <h3><i className="bi bi-check2-circle"></i> Savoir-faire</h3>
                                <ul className={styles.modalAchievementsList}>
                                    {selectedMember.savoirFaire.map((item, index) => (
                                        <li key={index}>
                                            <i className="bi bi-check-circle-fill"></i>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            )}

                            {selectedMember.achievements && selectedMember.achievements.length > 0 && (
                            <div className={styles.modalSection}>
                                <h3><i className="bi bi-check2-circle"></i> Savoir-faire</h3>
                                <ul className={styles.modalAchievementsList}>
                                    {selectedMember.achievements.map((achievement, index) => (
                                        <li key={index}>
                                            <i className="bi bi-check-circle-fill"></i>
                                            {achievement}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            )}

                            <div className={styles.modalActions}>
                                <button className={styles.modalContactButton}>
                                    <i className="bi bi-envelope"></i>
                                    Contacter {selectedMember.name.split(' ')[1]}
                                </button>
                                <button
                                    className={styles.modalCloseSecondary}
                                    onClick={() => setSelectedMember(null)}
                                >
                                    <i className="bi bi-x-circle"></i>
                                    Fermer
                                </button>
                            </div>
                        </div>
                        </div>{/* end modalScroll */}
                    </motion.div>
                </div>
            )}
        </section>
    );
};

export default Team;